import React from "react";
import {Checkbox, Input, message, Select, Table, Tag, Tooltip} from "antd";
import type {ColumnsType} from "antd/es/table";
import {JobCode} from "../../../../types/JobCodes";
import {ViewType} from "../../../../types/ViewType";
import {CopyOutlined, InfoCircleOutlined, QuestionOutlined} from "@ant-design/icons";

const { Option } = Select;

type StepFourProps = {
    jobCodes: JobCode[];
    setJobCodes: React.Dispatch<React.SetStateAction<JobCode[]>>;
    viewType?: ViewType;
};

const ProductAssignment: React.FC<StepFourProps> = ({ jobCodes, setJobCodes, viewType }) => {
    const handleCheckboxChange = (checked: boolean, record: JobCode, field: keyof JobCode) => {
        setJobCodes((prev) =>
            prev.map((jobCode) =>
                jobCode.alias === record.alias ? { ...jobCode, [field]: checked ? "1" : "0" } : jobCode
            )
        );
    };

    const handleInputChange = (value: string, record: JobCode, field: keyof JobCode) => {
        setJobCodes((prev) =>
            prev.map((jobCode) =>
                jobCode.alias === record.alias ? { ...jobCode, [field]: value } : jobCode
            )
        );
    };

    const handleDropdownChange = (
        value: string,
        record: JobCode,
        field: keyof JobCode
    ) => {
        setJobCodes((prev) =>
            prev.map((jobCode) =>
                jobCode.alias === record.alias
                    ? { ...jobCode, [field]: value }
                    : jobCode
            )
        );
    };

    const renderCell = (
        value: any,
        record: JobCode,
        key: keyof JobCode,
        renderInput: (value: any) => React.ReactNode
    ) => (
        <div
            style={{
                padding: '4px',
                borderRadius: '4px',
            }}
        >
            {renderInput(value)}
        </div>
    );

    const columns: ColumnsType<JobCode> = [
        {
            title: "Code",
            dataIndex: "alias",
            width: 60,
            key: "alias", sorter: (a, b) => a.alias.localeCompare(b.alias) },
        {
            title: "Bemerkung",
            dataIndex: "descriptionSpez",
            key: "descriptionSpez",
            width: 170,
            sorter: (a, b) => a.descriptionSpez.localeCompare(b.descriptionSpez) },
        {
            title: (
                <Tooltip title="Stunden in der Systemplanung">
                    <span>SP h</span>
                </Tooltip>
            ),
            dataIndex: 'productOwnerHours',
            key: 'productOwnerHours',
            width: 70,
            render: (value, record) =>
                renderCell(value, record, 'productOwnerHours', val => (
                    <Input
                        type="number"
                        value={val}
                        onChange={e => handleInputChange(e.target.value, record, 'productOwnerHours')}
                    />
                )),
        },
        {
            title: (
                <Tooltip title="Stunden in der Konstruktion">
                    <span>Kon h</span>
                </Tooltip>
            ),
            dataIndex: 'drawingHours',
            key: 'drawingHours',
            width: 70,
            render: (value, record) =>
                renderCell(value, record, 'drawingHours', val => (
                    <Input
                        type="number"
                        value={val}
                        onChange={e => handleInputChange(e.target.value, record, 'drawingHours')}
                    />
                )),
        },
        {
            title: (
                <Tooltip title="Manntage in der Montage">
                    <span>Mont. MT</span>
                </Tooltip>
            ),
            dataIndex: 'assemblyDays',
            key: 'assemblyDays',
            width: 70,
            render: (value, record) =>
                renderCell(value, record, 'assemblyDays', val => (
                    <Input
                        type="number"
                        value={val}
                        onChange={e => handleInputChange(e.target.value, record, 'assemblyDays')}
                    />
                )),
        },
        {
            title: "Systempl.",
            dataIndex: "hasSystemPlanning",
            key: "hasSystemPlanning",
            width: 100,
            render: (value, record) => (
                <Checkbox
                    checked={value === "1"}
                    onChange={(e) => handleCheckboxChange(e.target.checked, record, "hasSystemPlanning")}
                />
            ),
        },
        {
            title: (
                <Tooltip title="Zeichnen nach Theorie">
                    <span>ZnT</span>
                </Tooltip>
            ),
            dataIndex: "drawByTheory",
            key: "drawByTheory",
            width: 100,
            render: (value, record) => (
                <Checkbox
                    checked={value === "1"}
                    onChange={(e) => handleCheckboxChange(e.target.checked, record, "drawByTheory")}
                />
            ),
        },
        {
            title: "Handelspr.",
            dataIndex: "externalProduct",
            key: "externalProduct",
            width: 100,
            render: (value, record) => (
                <Checkbox
                    checked={value === "1"}
                    onChange={(e) => handleCheckboxChange(e.target.checked, record, "externalProduct")}
                />
            ),
        },
        {
            title: 'Verzinkt',
            dataIndex: 'galvanized',
            key: 'galvanized',
            width: 100,
            render: (value, record) => (
                <Checkbox
                    checked={value === "1"}
                    onChange={(e) => handleCheckboxChange(e.target.checked, record, "galvanized")}
                />
            ),
        },
        {
            title: 'Beschichtung',
            dataIndex: 'hasSurface',
            key: 'hasSurface',
            width: 100,
            render: (value, record) => (
                <Checkbox
                    checked={value === "1"}
                    onChange={(e) => handleCheckboxChange(e.target.checked, record, "hasSurface")}
                />
            ),
        },
        {
            title: "Vormontage",
            dataIndex: "preAssembly",
            key: "preAssembly",
            width: 170,
            render: (value, record) => (
                <Checkbox
                    checked={value === "1"}
                    onChange={(e) => handleCheckboxChange(e.target.checked, record, "preAssembly")}
                />
            ),
        },
        {
            title: "Montage",
            dataIndex: "assemblyType",
            key: "assemblyType",
            width: 170,
            render: (_, record) => (
                <Select
                    style={{ width: "100%" }}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    disabled={viewType === ViewType.NEW_EXTERNAL}
                    value={record.assemblyType || "Eigene Montage"}
                    onChange={(value) => handleDropdownChange(value, record, "assemblyType")}
                >
                    <Option value="Eigene Montage">Eigene Montage</Option>
                    <Option value="Fremdmontage">Fremdmontage</Option>
                    <Option value="Eigen- und Fremdmontage">Eigen- und Fremdmontage</Option>
                    <Option value="Nur Lieferung">Nur Lieferung</Option>
                </Select>
            ),
        },
        {
            title: "Einbauort",
            dataIndex: "installationLocation",
            key: "installationLocation",
            width: 170,
            render: (_, record) => (
                <Select
                    style={{ width: "100%" }}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    disabled={viewType === ViewType.NEW_EXTERNAL}
                    value={record.installationLocation || "Innen"}
                    onChange={(value) => handleDropdownChange(value, record, "installationLocation")}
                >
                    <Option value="Innen">Innen</Option>
                    <Option value="Aussen">Aussen</Option>
                    <Option value="Umgebung">Umgebung</Option>
                </Select>
            ),
        },
        {
            title: 'Farbcode',
            dataIndex: 'colorCode',
            key: 'colorCode',
            width: 220,
            render: (value, record) => {
                const tags = value ? value.split('|') : [];

                const handleSetUnknown = () => {
                    const newVal = 'Noch nicht bekannt';
                    handleInputChange(newVal, record, 'colorCode');
                };

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Select
                            mode="tags"
                            style={{ width: '100%' }}
                            placeholder="Farben eingeben"
                            value={tags}
                            onChange={vals => handleInputChange(vals.join('|'), record, 'colorCode')}
                            open={false}
                            tagRender={({ label, closable, onClose }) => {
                                const fullLabel = label?.toString() ?? '';
                                const shortLabel = fullLabel.length > 10 ? fullLabel.slice(0, 10) + '…' : fullLabel;

                                const handleCopy = (e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(fullLabel)
                                        .then(() => message.success('Farbcode kopiert!'))
                                        .catch(() => message.error('Kopieren fehlgeschlagen.'));
                                };

                                return (
                                    <Tooltip title={fullLabel}>
                                        <Tag
                                            closable={closable}
                                            onClose={onClose}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                margin: '2px',
                                                paddingInline: 8,
                                                paddingBlock: 4,
                                                lineHeight: '18px',
                                                userSelect: 'text',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <span>{shortLabel}</span>
                                            <CopyOutlined
                                                onClick={handleCopy}
                                                style={{ cursor: 'pointer', fontSize: 12 }}
                                                title="Farbcode kopieren"
                                            />
                                        </Tag>
                                    </Tooltip>
                                );
                            }}
                        />
                        <QuestionOutlined
                            onClick={handleSetUnknown}
                            style={{ cursor: 'pointer', color: '#888' }}
                            title="Farbcode auf 'Noch nicht bekannt' setzen"
                        />
                    </div>
                );
            },
        }
    ];

    return (
        <div>
            <Table
                columns={columns}
                dataSource={jobCodes}
                rowKey="alias"
                pagination={{ pageSize: 100 }}
                scroll={{ x: "max-content" }}
            />
        </div>
    );
};

export default ProductAssignment;
